<template>
	<DexDataTable class="p-component p-shadow-2"
		:value="allElements"
		:rowHover="true"
		:autoLayout="true"
		:paginator="true"
		:lazy="true"
		:loading="!loaded"
		paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:rows="pagination.pageSize"
		:totalRecords="pagination.total"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords} '"
		@page="onPage($event)"
		@row-click="onMarkLines($event)"
		@row-hover="onMarkLines($event)"
		@rowHover="true"
		columnResizeMode="fit"
		:scrollable="true"
		scrollHeight="flex"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Validation.General.Position') }) }}
		</template>
		<Column field="value" :header="$t('Squeeze.Locators.Value')" style="min-width: 5rem; max-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.top="slotProps.data.value">
					{{ slotProps.data.value }}
				</div>
			</template>
		</Column>
		<Column field="line" :header="$t('Squeeze.DocumentClasses.Line')"></Column>
		<Column field="boundingBox.page" :header="$t('Squeeze.Locators.Site')"></Column>
		<Column field="boundingBox.x0" header="x0"></Column>
		<Column field="boundingBox.y0" header="y0"></Column>
		<Column field="boundingBox.x1" header="x1"></Column>
		<Column field="boundingBox.y1" header="y1"></Column>

		<template #paginatorstart>
			<Button type="button" icon="mdi mdi-refresh" class="p-button-text" @click="onReload" :disabled="!loaded" />
		</template>
		<template #paginatorend></template>
	</DexDataTable>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref } from 'vue';
import {ClientManager} from "@/singletons/ClientManager";
import { useI18n } from 'vue-i18n'
import { useToast } from "primevue/usetoast";
import {ToastManager} from "@/util/ToastManager";
import DexDataTable from "@/components/datatable/DexDataTable.vue";
import Column from 'primevue/column';
import {PaginationDto} from "@dex/squeeze-client-ts";
import {DocumentSpan} from "@dex/squeeze-client-ts";
import {useRoute} from "vue-router";
import useSqueezeStore from "@/apps/squeeze/store";
import {TableSettings} from "@/util/TableSettings";

export default defineComponent({
	name: 'OCRLines',
	components: {
		DexDataTable,
		Column,
	},
	props: {
		documentId: {
			type: Number,
			default: 0,
			required: true,
		},
	},
	emits: [
		'onMarkLines',
	],

	setup(props, { emit }) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** Indicates end of request */
		const loaded = ref(false);

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Document Class API endpoint */
		const documentApi = ClientManager.getInstance().squeeze.document;

		/** Empty pagination object & Info / Options of pagination  */
		const pagination = reactive<PaginationDto>({});

		/** Empty element array */
		const allElements = ref<DocumentSpan[]>([]);

		/** Get all Document Spans */
		const getDocumentSpans = () => {
			loaded.value = false;
			documentApi.getDocumentLines(props.documentId, pagination.page, pagination.pageSize)
				.then((documentSpans) => {
					allElements.value = documentSpans.elements as DocumentSpan[];
					Object.assign(pagination, documentSpans.pagination)
				})
				.catch(response => response.json().then ((err: any) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				}))
				.finally(() => {
					loaded.value = true;
				})
		}

		/** Triggered when the next page is selected */
		const onPage = (event: {page: number; rows: number}) => {
			pagination.page = event.page;
			pagination.pageSize = event.rows;

			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name as string + '/ocrLines', event.rows);
			getDocumentSpans();
		}

		/** Triggered when table content should be reloaded */
		const onReload = () => {
			getDocumentSpans();
		}

		/**
		 * Click a lines to mark
		 * @param event
		 */
		const onMarkLines = (event: any) => {
			emit("onMarkLines", event);
		}

		onMounted(() => {
			// Initialize pagination info / options
			pagination.pageSize = TableSettings.getTableListPagination(store, route.name as string + '/ocrLines');
			pagination.page = 0;
			pagination.total = 0;

			getDocumentSpans();
		});

		return {
			t,
			toast,
			store,
			documentApi,
			loaded,
			allElements,
			pagination,
			getDocumentSpans,
			onPage,
			onReload,
			onMarkLines,
		};
	},
});

</script>

<style lang="scss" scoped>

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

</style>
